/**
 * TrackerBatchTable
 *
 * Table with expandable batch rows for the Production Tracker.
 * Each row shows batch info; clicking expands to show orders/garments
 * directly beneath that batch row (not at the bottom of the table).
 *
 * Supports archive/restore actions for admin users.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, X, ChevronRight, MoreHorizontal, ExternalLink, Archive, ArchiveRestore, PackageCheck, Receipt } from 'lucide-react';
import { formatDistanceToNow, format, differenceInHours, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { useBatchOrders, useArchiveBatch, useRestoreBatch, useMarkOrderShipped } from '../hooks/useProductionTracker';
import { TrackerOrderRow } from './TrackerOrderRow';
import { useCanManageFloor } from '../hooks/useCanManageFloor';
import { useTrackerClientOrg } from '../hooks/useTrackerClientOrg';
import { useStationSettings } from '@/features/production-settings/hooks/useStationSettings';
import { StatusOverrideDialog, type OverrideTarget } from './StatusOverrideDialog';

// ============================================================================
// TYPES
// ============================================================================

export interface BatchRecord {
  id: string;
  batch_number: number;
  name: string | null;
  status: string;
  total_orders: number;
  total_pod_items: number;
  total_pap_items: number;
  total_redo_items: number;
  picklist_printed: boolean;
  labels_printed: boolean;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  archived_at?: string | null;
}

interface TrackerBatchTableProps {
  batches: BatchRecord[];
  isLoading?: boolean;
  /** Whether this table is showing archived batches */
  isArchived?: boolean;
  /** Location ID for archive/restore mutations */
  locationId?: string;
  /** Shopify domain for order links */
  shopifyDomain?: string | null;
  /** Future v2: batch-level actions */
  onBatchAction?: (batchId: string, action: string) => void;
  /** Whether invoice printing is enabled for this location (BLU-328) */
  invoiceEnabled?: boolean;
  /** Callback to print invoices for a batch (BLU-328) */
  onPrintBatchInvoices?: (batchId: string) => void;
  /** Callback to print invoice for a single order (BLU-328) */
  onPrintOrderInvoice?: (orderId: string) => void;
}

// ============================================================================
// STATUS COLORS
// ============================================================================

const statusColors: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PICKING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  PICKED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  CATCHING: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  PACKING: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  CANCELLED: 'bg-slate-100 text-slate-800 dark:bg-slate-800/30 dark:text-slate-400',
};

// ============================================================================
// HELPERS
// ============================================================================

function getGarmentCount(batch: BatchRecord): number {
  return batch.total_pod_items + batch.total_pap_items + batch.total_redo_items;
}

function getBatchType(batch: BatchRecord): { labelKey: string; className: string } {
  const hasPod = batch.total_pod_items > 0;
  const hasPap = batch.total_pap_items > 0;
  if (hasPod && hasPap) return { labelKey: 'tracker.batches.batchType.mixed', className: 'border-violet-400 text-violet-600 dark:text-violet-400' };
  if (hasPap) return { labelKey: 'tracker.batches.batchType.pap', className: 'border-emerald-400 text-emerald-600 dark:text-emerald-400' };
  return { labelKey: 'tracker.batches.batchType.pod', className: 'border-blue-400 text-blue-600 dark:text-blue-400' };
}

function getTimeInStage(batch: BatchRecord): { text: string; isStale: boolean } {
  const refDate = new Date(batch.updated_at);
  const hoursAgo = differenceInHours(new Date(), refDate);
  return {
    text: formatDistanceToNow(refDate, { addSuffix: false }),
    isStale: hoursAgo >= 2,
  };
}

/** Check if archived batch can be restored (within 30-day window) */
function isRestorable(batch: BatchRecord): boolean {
  if (!batch.archived_at) return false;
  return differenceInDays(new Date(), new Date(batch.archived_at)) < 30;
}

// ============================================================================
// EXPANDED BATCH CONTENT
// ============================================================================

function ExpandedBatchContent({
  batchId,
  shopifyDomain,
  onMarkShipped,
  hasOverrideAccess,
  onOverrideStatus,
  invoiceEnabled,
  onPrintOrderInvoice,
}: {
  batchId: string;
  shopifyDomain: string | null;
  onMarkShipped: (orderId: string, orderName: string) => void;
  hasOverrideAccess?: boolean;
  onOverrideStatus?: (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => void;
  invoiceEnabled?: boolean;
  onPrintOrderInvoice?: (orderId: string) => void;
}) {
  const { t } = useTranslation('production');
  const { data, isLoading, error } = useBatchOrders(batchId);
  // BLU-367: a cross-org batch carries orders from several client orgs.
  // Resolved once per expanded batch, not per sub-row.
  const clientOrg = useTrackerClientOrg();
  // BLU-1412 / BLU-1462: the floor owner's calls, resolved once per batch.
  const canManageFloor = useCanManageFloor();
  // BLU-1509: read once per batch (cached), for every row's order switch menu.
  const { settings: stationSettings } = useStationSettings({ enabled: canManageFloor });

  if (isLoading) {
    return (
      <div className="py-4 px-6 space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-16 ml-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 px-6 text-sm text-destructive">
        {t('tracker.batches.failedToLoad')}
      </div>
    );
  }

  if (!data?.orders || data.orders.length === 0) {
    return (
      <div className="py-4 px-6 text-sm text-muted-foreground">
        {t('tracker.batches.noOrders')}
      </div>
    );
  }

  return (
    <div className="px-2 py-0.5">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs h-8 pb-1 w-[180px]">{t('tracker.orders.header.order')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.orders.header.customer')}</TableHead>
            <TableHead className="text-xs h-8 pb-1 text-right w-[80px]">{t('tracker.orders.header.items')}</TableHead>
            <TableHead className="text-xs h-8 pb-1">{t('tracker.orders.header.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.orders.map((order) => (
            <TrackerOrderRow
              key={order.id}
              order={order}
              clientOrg={clientOrg}
              canManageFloor={canManageFloor}
              dtfEnabled={stationSettings.dtfEnabled}
              batchId={batchId}
              shopifyDomain={shopifyDomain}
              onMarkShipped={onMarkShipped}
              hasOverrideAccess={hasOverrideAccess}
              onOverrideStatus={onOverrideStatus}
              invoiceEnabled={invoiceEnabled}
              onPrintOrderInvoice={onPrintOrderInvoice}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================================================
// BATCH ROW + EXPANSION
// ============================================================================

function BatchRowGroup({
  batch,
  isExpanded,
  onToggle,
  isArchived,
  locationId,
  shopifyDomain,
  onMarkShipped,
  onMarkAllShipped,
  hasOverrideAccess,
  onOverrideStatus,
  invoiceEnabled,
  onPrintBatchInvoices,
  onPrintOrderInvoice,
}: {
  batch: BatchRecord;
  isExpanded: boolean;
  onToggle: () => void;
  isArchived?: boolean;
  locationId?: string;
  shopifyDomain?: string | null;
  onMarkShipped: (orderId: string, orderName: string) => void;
  onMarkAllShipped: (batchId: string) => void;
  hasOverrideAccess?: boolean;
  onOverrideStatus?: (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => void;
  invoiceEnabled?: boolean;
  onPrintBatchInvoices?: (batchId: string) => void;
  onPrintOrderInvoice?: (orderId: string) => void;
}) {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const { isAdmin } = useRole();
  const archiveMutation = useArchiveBatch();
  const restoreMutation = useRestoreBatch();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const batchType = getBatchType(batch);
  const timeInStage = getTimeInStage(batch);
  const canRestore = isArchived && isRestorable(batch);

  const handleArchive = () => {
    if (!locationId) return;
    archiveMutation.mutate({ batchId: batch.id, locationId });
    setShowArchiveDialog(false);
  };

  const handleRestore = () => {
    if (!locationId) return;
    restoreMutation.mutate({ batchId: batch.id, locationId });
  };

  return (
    <>
      <TableRow
        className={cn(
          'cursor-pointer hover:bg-muted/50 transition-colors group',
          isExpanded && 'bg-muted/30'
        )}
        onClick={onToggle}
        tabIndex={0}
        aria-label={`Batch ${batch.batch_number}, ${batch.total_orders} orders`}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell className="w-10 px-3">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <span>BATCH-{String(batch.batch_number).padStart(3, '0')}</span>
            <Badge
              variant="secondary"
              className={statusColors[batch.status] ?? statusColors.OPEN}
            >
              {batch.status}
            </Badge>
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {batch.total_orders}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {getGarmentCount(batch)}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={cn('text-xs', batchType.className)}>
            {t(batchType.labelKey)}
          </Badge>
        </TableCell>
        <TableCell>
          {isArchived && batch.archived_at ? (
            <span className="text-sm text-muted-foreground">
              {formatDistanceToNow(new Date(batch.archived_at), { addSuffix: true })}
            </span>
          ) : (
            <span
              className={cn(
                'text-sm tabular-nums',
                timeInStage.isStale
                  ? 'text-amber-600 dark:text-amber-400 font-medium'
                  : 'text-muted-foreground'
              )}
              title={format(new Date(batch.updated_at), 'PPpp')}
            >
              {timeInStage.text}
            </span>
          )}
        </TableCell>
        <TableCell>
          {batch.picklist_printed ? (
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground">
              <X className="h-4 w-4" />
            </div>
          )}
        </TableCell>
        <TableCell>
          {batch.labels_printed ? (
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground">
              <X className="h-4 w-4" />
            </div>
          )}
        </TableCell>
        <TableCell className="text-muted-foreground">
          <time
            dateTime={batch.created_at}
            title={format(new Date(batch.created_at), 'PPpp')}
          >
            {formatDistanceToNow(new Date(batch.created_at), {
              addSuffix: true,
            })}
          </time>
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8 transition-opacity',
                  locationId && batch.status === 'PACKING'
                    ? 'opacity-60 hover:opacity-100'
                    : 'opacity-0 group-hover:opacity-100'
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('tracker.batches.batchActions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/production/batches/${batch.id}`);
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('tracker.batches.viewDetails')}
              </DropdownMenuItem>
              {invoiceEnabled && onPrintBatchInvoices && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrintBatchInvoices(batch.id);
                    }}
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    {t('tracker.invoice.printOrderInvoices')}
                  </DropdownMenuItem>
                </>
              )}
              {locationId && batch.status === 'PACKING' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAllShipped(batch.id);
                    }}
                  >
                    <PackageCheck className="h-4 w-4 mr-2" />
                    {t('tracker.batches.markAllShipped')}
                  </DropdownMenuItem>
                </>
              )}
              {isAdmin() && locationId && !isArchived && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowArchiveDialog(true);
                    }}
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    {t('tracker.batches.archiveBatch')}
                  </DropdownMenuItem>
                </>
              )}
              {isAdmin() && locationId && canRestore && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestore();
                    }}
                  >
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    {t('tracker.batches.restoreBatch')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      {/* Expanded orders — rendered directly after this batch row */}
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={10} className="p-0">
              <motion.div
                initial={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                animate={shouldReduceMotion ? {} : { opacity: 1, height: 'auto' }}
                exit={shouldReduceMotion ? {} : { opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-muted/10 border-b"
              >
                <ExpandedBatchContent
                  batchId={batch.id}
                  shopifyDomain={shopifyDomain ?? null}
                  onMarkShipped={onMarkShipped}
                  hasOverrideAccess={hasOverrideAccess}
                  onOverrideStatus={onOverrideStatus}
                  invoiceEnabled={invoiceEnabled}
                  onPrintOrderInvoice={onPrintOrderInvoice}
                />
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>

      {/* Archive confirmation dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('tracker.batches.archiveConfirm.title', { number: batch.batch_number })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('tracker.batches.archiveConfirm.desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('tracker.batches.archiveConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              {t('tracker.batches.archiveConfirm.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TrackerBatchTable({
  batches,
  isLoading,
  isArchived,
  locationId,
  shopifyDomain,
  onBatchAction: _onBatchAction,
  invoiceEnabled,
  onPrintBatchInvoices,
  onPrintOrderInvoice,
}: TrackerBatchTableProps) {
  const { t } = useTranslation('production');
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const { isAdmin, isWarehouseCoordinator } = useRole();
  const hasOverrideAccess = isAdmin() || isWarehouseCoordinator();
  const [overrideTarget, setOverrideTarget] = useState<OverrideTarget | null>(null);

  // Mark shipped state
  const markShippedMutation = useMarkOrderShipped();
  const [shipConfirm, setShipConfirm] = useState<{ orderId: string; orderName: string } | null>(null);
  const [markAllBatchId, setMarkAllBatchId] = useState<string | null>(null);
  const [markAllProgress, setMarkAllProgress] = useState<{ done: number; total: number } | null>(null);

  const toggleExpand = (batchId: string) => {
    setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
  };

  const handleMarkShipped = (orderId: string, orderName: string) => {
    setShipConfirm({ orderId, orderName });
  };

  const confirmMarkShipped = () => {
    if (!shipConfirm || !locationId) return;
    markShippedMutation.mutate(
      { orderId: shipConfirm.orderId, locationId },
      { onSettled: () => setShipConfirm(null) }
    );
  };

  const handleOverrideStatus = (target: { orderId: string; orderName: string; currentStatus: string; locationId: string }) => {
    setOverrideTarget({ type: 'order', ...target });
  };

  const handleMarkAllShipped = async (batchId: string) => {
    setMarkAllBatchId(batchId);
  };

  const confirmMarkAllShipped = async () => {
    if (!markAllBatchId || !locationId) return;

    const cached = trpcUtils.productionOrders.getBatchOrdersWithGarments.getData({ batchId: markAllBatchId });
    const eligibleOrders = (cached?.orders ?? []).filter(
      (o) => o.productionStatus === 'PACKED' || o.productionStatus === 'READY_TO_PACK'
    );

    if (eligibleOrders.length === 0) {
      setMarkAllBatchId(null);
      return;
    }

    setMarkAllProgress({ done: 0, total: eligibleOrders.length });

    for (let i = 0; i < eligibleOrders.length; i++) {
      const order = eligibleOrders[i]!;
      try {
        await markShippedMutation.mutateAsync({
          orderId: order.id,
          locationId,
        });
      } catch {
        // Continue with remaining orders even if one fails
      }
      setMarkAllProgress({ done: i + 1, total: eligibleOrders.length });
    }

    setMarkAllBatchId(null);
    setMarkAllProgress(null);
  };

  // We need tRPC utils for reading cached data in Mark All
  const trpcUtils = trpc.useUtils();

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <div className="space-y-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4 py-3.5 px-4 border-b last:border-0">
              <Skeleton className="h-6 w-36" />
              <Skeleton className="h-6 w-8 ml-auto" />
              <Skeleton className="h-6 w-8" />
              <Skeleton className="h-6 w-14" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-8" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (batches.length === 0) {
    return null; // Parent handles empty state
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><span className="sr-only">{t('tracker.batches.header.expand')}</span></TableHead>
              <TableHead>{t('tracker.batches.header.batch')}</TableHead>
              <TableHead className="text-right">{t('tracker.batches.header.orders')}</TableHead>
              <TableHead className="text-right">{t('tracker.batches.header.garments')}</TableHead>
              <TableHead>{t('tracker.batches.header.type')}</TableHead>
              <TableHead>{isArchived ? t('tracker.batches.header.archived') : t('tracker.batches.header.timeInStage')}</TableHead>
              <TableHead>{t('tracker.batches.header.picklist')}</TableHead>
              <TableHead>{t('tracker.batches.header.labels')}</TableHead>
              <TableHead>{t('tracker.batches.header.created')}</TableHead>
              <TableHead className="w-10"><span className="sr-only">{t('tracker.batches.header.actions')}</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((batch) => (
              <BatchRowGroup
                key={batch.id}
                batch={batch}
                isExpanded={expandedBatchId === batch.id}
                onToggle={() => toggleExpand(batch.id)}
                isArchived={isArchived}
                locationId={locationId}
                shopifyDomain={shopifyDomain}
                onMarkShipped={handleMarkShipped}
                onMarkAllShipped={handleMarkAllShipped}
                hasOverrideAccess={hasOverrideAccess}
                onOverrideStatus={handleOverrideStatus}
                invoiceEnabled={invoiceEnabled}
                onPrintBatchInvoices={onPrintBatchInvoices}
                onPrintOrderInvoice={onPrintOrderInvoice}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Single order mark shipped confirmation */}
      <AlertDialog open={!!shipConfirm} onOpenChange={(open) => !open && setShipConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('tracker.batches.shipConfirm.title', { orderName: shipConfirm?.orderName })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('tracker.batches.shipConfirm.desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('tracker.batches.shipConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMarkShipped}
              disabled={markShippedMutation.isPending}
            >
              {markShippedMutation.isPending ? t('tracker.batches.shipConfirm.marking') : t('tracker.batches.shipConfirm.markShipped')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch-level mark all shipped confirmation */}
      <AlertDialog open={!!markAllBatchId} onOpenChange={(open) => !open && setMarkAllBatchId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('tracker.batches.markAllConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {markAllProgress
                ? t('tracker.batches.markAllConfirm.progress', { done: markAllProgress.done, total: markAllProgress.total })
                : t('tracker.batches.markAllConfirm.desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!markAllProgress}>{t('tracker.batches.markAllConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMarkAllShipped}
              disabled={!!markAllProgress}
            >
              {markAllProgress ? `${markAllProgress.done}/${markAllProgress.total}` : t('tracker.batches.markAllConfirm.markAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status override dialog */}
      {overrideTarget && (
        <StatusOverrideDialog
          open={!!overrideTarget}
          onOpenChange={(open) => { if (!open) setOverrideTarget(null); }}
          target={overrideTarget}
        />
      )}
    </>
  );
}
